if (!customElements.get('customer-reviews')) {
  class CustomerReviews extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !['theme_managed', 'imported'].includes(this.dataset.source)) return;
      this.initialized = true;
      this.pageSize = Math.max(1, Number(this.dataset.pageSize) || 5);
      this.paginationStyle = this.dataset.paginationStyle || 'numbered';
      this.currentPage = 1;
      this.visibleCount = this.pageSize;
      this.searchTerm = '';
      this.searchTimer = null;
      this.activeMediaIndex = -1;
      this.lastMediaTrigger = null;
      this.pointerStartX = null;

      this.reviewList = this.querySelector('[data-review-list]');
      this.reviews = Array.from(this.querySelectorAll('[data-review-item]'));
      this.results = this.querySelector('[data-review-results]');
      this.pagination = this.querySelector('[data-review-pagination]');
      this.loadMoreButton = this.querySelector('[data-review-load-more]');
      this.noResults = this.querySelector('[data-review-no-results]');
      this.emptyState = this.querySelector('[data-review-empty]');
      this.searchInput = this.querySelector('[data-review-search]');
      this.searchClear = this.querySelector('[data-review-search-clear]');
      this.sortSelect = this.querySelector('[data-review-sort]');
      this.clearFiltersButton = this.querySelector('[data-review-clear-filters]');
      this.filterCount = this.querySelector('[data-filter-count]');
      this.lightbox = this.querySelector('[data-review-lightbox]');
      this.lightboxContent = this.querySelector('[data-lightbox-content]');
      this.lightboxPosition = this.querySelector('[data-lightbox-position]');

      this.handleClick = this.onClick.bind(this);
      this.handleChange = this.onChange.bind(this);
      this.handleInput = this.onInput.bind(this);
      this.handleKeydown = this.onKeydown.bind(this);
      this.handleDialogClose = this.onDialogClose.bind(this);
      this.handlePointerDown = (event) => {
        this.pointerStartX = event.clientX;
      };
      this.handlePointerUp = (event) => {
        if (this.pointerStartX === null || this.activeMediaIndex < 0) return;
        const distance = event.clientX - this.pointerStartX;
        this.pointerStartX = null;
        if (Math.abs(distance) < 50) return;
        this.showAdjacentMedia(distance < 0 ? 1 : -1);
      };

      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
      this.addEventListener('input', this.handleInput);
      this.addEventListener('keydown', this.handleKeydown);
      this.lightbox?.addEventListener('close', this.handleDialogClose);
      this.lightboxContent?.addEventListener('pointerdown', this.handlePointerDown);
      this.lightboxContent?.addEventListener('pointerup', this.handlePointerUp);

      if (this.sortSelect) this.sortSelect.value = this.dataset.defaultSort || 'recent';
      if (this.dataset.source === 'imported' && !['recent', 'oldest', 'highest', 'lowest'].includes(this.sortSelect?.value || this.dataset.defaultSort)) {
        this.dataset.defaultSort = 'recent';
        if (this.sortSelect) this.sortSelect.value = 'recent';
      }
      this.setupInfiniteLoading();
      if (this.dataset.source === 'imported') this.loadImportedReviews();
      else this.applyReviews();
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('input', this.handleInput);
      this.removeEventListener('keydown', this.handleKeydown);
      this.lightbox?.removeEventListener('close', this.handleDialogClose);
      this.lightboxContent?.removeEventListener('pointerdown', this.handlePointerDown);
      this.lightboxContent?.removeEventListener('pointerup', this.handlePointerUp);
      this.infiniteObserver?.disconnect();
      this.importController?.abort();
      this.importController = null;
      window.clearTimeout(this.searchTimer);
      document.body.classList.remove('customer-reviews-lightbox-open');
      this.initialized = false;
    }

    async loadImportedReviews() {
      this.importController?.abort();
      const controller = new AbortController();
      this.importController = controller;
      this.importState = 'loading';
      this.setAttribute('aria-busy', 'true');
      this.toggleAttribute('data-import-ready', false);
      this.reviewList.replaceChildren();
      this.reviews = [];
      this.applyReviews();
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(this.dataset.importUrl, { signal: controller.signal, credentials: 'omit' });
        if (!response.ok) throw new Error(`Review file returned ${response.status}`);
        const records = await response.json();
        if (!Array.isArray(records)) throw new Error('Expected an array of reviews');
        const ids = new Set();
        records.forEach((record, index) => {
          const validDate = typeof record?.review_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(record.review_date)
            && !Number.isNaN(Date.parse(record.review_date))
            && new Date(record.review_date).toISOString().slice(0, 10) === record.review_date;
          if (!record || typeof record.review_id !== 'string' || !record.review_id.trim() || ids.has(record.review_id)
            || typeof record.reviewer_name !== 'string' || !record.reviewer_name.trim()
            || typeof record.body !== 'string' || !record.body.trim()
            || !Number.isInteger(record.rating) || record.rating < 1 || record.rating > 5 || !validDate) {
            throw new Error(`Invalid or duplicate review at row ${index + 1}`);
          }
          ids.add(record.review_id);
        });
        if (controller !== this.importController || !this.isConnected) return;
        const template = this.querySelector('[data-import-review-template]');
        if (!template || !this.reviewList) throw new Error('Missing imported review template');
        const fragment = document.createDocumentFragment();
        records.forEach((record, index) => {
          const card = template.content.firstElementChild.cloneNode(true);
          Object.assign(card.dataset, {
            reviewId: record.review_id, originalIndex: String(index), rating: String(record.rating),
            date: record.review_date, helpful: '0', verified: 'false', hasPhoto: 'false', hasVideo: 'false',
            productId: this.dataset.importProductId || '',
            search: `${record.reviewer_name} ${record.body}`.toLowerCase(),
          });
          const name = card.querySelector('[data-import-name]');
          if (name) name.textContent = record.reviewer_name;
          const time = card.querySelector('[data-import-date]');
          if (time) {
            time.dateTime = record.review_date;
            time.textContent = record.review_date;
          }
          const title = card.querySelector('[data-import-title]');
          if (title) {
          title.textContent = typeof record.title === 'string' ? record.title : '';
          title.hidden = !title.textContent.trim();
          }
          card.querySelector('[data-import-body]').textContent = record.body;
          const stars = card.querySelector('[data-import-stars]');
          stars.setAttribute('aria-label', `${record.rating} out of 5 stars`);
          Array.from(stars.children).forEach((star, position) => star.classList.toggle('is-empty', position >= record.rating));
          fragment.appendChild(card);
        });
        this.reviewList.replaceChildren(fragment);
        this.reviews = Array.from(this.reviewList.querySelectorAll('[data-review-item]'));
        this.updateImportedSummary(records);
        this.importState = 'ready';
        this.toggleAttribute('data-import-ready', true);
        this.applyReviews();
      } catch (error) {
        if (controller !== this.importController || !this.isConnected) return;
        this.importState = 'error';
        this.applyReviews();
        console.warn('Customer reviews import failed:', error.message);
      } finally {
        window.clearTimeout(timeout);
        if (controller === this.importController) this.removeAttribute('aria-busy');
      }
    }

    updateImportedSummary(records) {
      const total = records.length;
      const average = total ? records.reduce((sum, record) => sum + record.rating, 0) / total : 0;
      this.dataset.reviewCount = String(total);
      this.dataset.averageRating = average.toFixed(1);
      this.querySelectorAll('[data-import-count]').forEach((element) => { element.textContent = String(total); });
      const number = this.querySelector('.customer-reviews__average-number');
      if (number) number.textContent = average.toFixed(1);
      const accessibleAverage = this.querySelector('.customer-reviews__average .visually-hidden');
      if (accessibleAverage) accessibleAverage.textContent = `${average.toFixed(1)} out of 5 stars`;
      const totalLabel = this.querySelector('.customer-reviews__total');
      if (totalLabel) totalLabel.textContent = `${total} ${total === 1 ? 'review' : 'reviews'}`;
      this.querySelectorAll('[data-import-rating]').forEach((row) => {
        const rating = Number(row.dataset.importRating);
        const count = records.filter((record) => record.rating === rating).length;
        const percent = total ? count * 100 / total : 0;
        row.querySelector('.customer-reviews__distribution-count').textContent = String(count);
        const track = row.querySelector('.customer-reviews__distribution-track');
        track.setAttribute('aria-label', `${rating} stars: ${count} reviews, ${Math.round(percent)} percent`);
        track.firstElementChild.style.width = `${percent}%`;
      });
    }


    onClick(event) {
      const tab = event.target.closest('[data-review-tab]');
      if (tab) {
        this.activateTab(tab);
        return;
      }

      if (event.target.closest('[data-review-search-submit]')) {
        this.updateSearch();
        return;
      }

      if (event.target.closest('[data-review-search-clear]')) {
        if (this.searchInput) this.searchInput.value = '';
        this.updateSearch();
        this.searchInput?.focus();
        return;
      }

      if (event.target.closest('[data-review-clear-filters]')) {
        this.querySelectorAll('[data-rating-filter], [data-feature-filter]').forEach((input) => {
          input.checked = false;
        });
        this.currentPage = 1;
        this.visibleCount = this.pageSize;
        this.applyReviews();
        return;
      }

      const pageButton = event.target.closest('[data-review-page]');
      if (pageButton) {
        this.currentPage = Number(pageButton.dataset.reviewPage) || 1;
        this.applyReviews({ announce: true });
        this.querySelector('[data-review-panel="reviews"]')?.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start',
        });
        return;
      }

      if (event.target.closest('[data-review-load-more]')) {
        this.revealMore(true);
        return;
      }

      const imageButton = event.target.closest('[data-review-image]');
      if (imageButton) {
        this.openImage(imageButton);
        return;
      }

      const videoButton = event.target.closest('[data-review-video]');
      if (videoButton) {
        this.openVideo(videoButton);
        return;
      }

      if (event.target.closest('[data-lightbox-close]')) {
        this.lightbox?.close();
        return;
      }

      if (event.target.closest('[data-lightbox-previous]')) {
        this.showAdjacentMedia(-1);
        return;
      }

      if (event.target.closest('[data-lightbox-next]')) {
        this.showAdjacentMedia(1);
        return;
      }

      if (event.target.closest('[data-review-provider-action]')) {
        const provider = this.querySelector('[data-review-provider-region]');
        if (!provider) return;
        provider.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start',
        });
        provider.querySelector('button, a, input, select, textarea')?.focus({ preventScroll: true });
      }
    }

    onChange(event) {
      if (event.target.matches('[data-rating-filter], [data-feature-filter], [data-review-sort]')) {
        this.currentPage = 1;
        this.visibleCount = this.pageSize;
        this.applyReviews();
      }
    }

    onInput(event) {
      if (!event.target.matches('[data-review-search]')) return;
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this.updateSearch(), 250);
    }

    onKeydown(event) {
      const activeTab = event.target.closest('[data-review-tab]');
      if (activeTab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        const tabs = Array.from(this.querySelectorAll('[data-review-tab]'));
        if (tabs.length < 2) return;
        event.preventDefault();
        const currentIndex = tabs.indexOf(activeTab);
        let nextIndex = currentIndex;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        this.activateTab(tabs[nextIndex]);
        tabs[nextIndex].focus();
        return;
      }

      if (!this.lightbox?.open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.lightbox.close();
        return;
      }
      if (event.key === 'ArrowLeft') this.showAdjacentMedia(-1);
      if (event.key === 'ArrowRight') this.showAdjacentMedia(1);
      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        this.lightbox.querySelectorAll('button:not([disabled]), a[href], video[controls], iframe')
      ).filter((element) => !element.hidden);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    activateTab(tab) {
      this.querySelectorAll('[data-review-tab]').forEach((candidate) => {
        const selected = candidate === tab;
        candidate.classList.toggle('is-active', selected);
        candidate.setAttribute('aria-selected', String(selected));
        candidate.tabIndex = selected ? 0 : -1;
      });
      this.querySelectorAll('[data-review-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.reviewPanel !== tab.dataset.reviewTab;
      });
    }

    updateSearch() {
      this.searchTerm = (this.searchInput?.value || '').trim().toLowerCase();
      if (this.searchClear) this.searchClear.hidden = !this.searchTerm;
      this.currentPage = 1;
      this.visibleCount = this.pageSize;
      this.applyReviews();
    }

    getFilteredReviews() {
      const ratings = Array.from(this.querySelectorAll('[data-rating-filter]:checked')).map((input) => input.value);
      const features = Array.from(this.querySelectorAll('[data-feature-filter]:checked')).map((input) => input.value);
      const currentProductId = this.dataset.currentProductId || '';
      const sort = this.sortSelect?.value || this.dataset.defaultSort || 'recent';

      const filtered = this.reviews.filter((review) => {
        if (this.searchTerm && !review.dataset.search.includes(this.searchTerm)) return false;
        if (ratings.length && !ratings.includes(review.dataset.rating)) return false;
        if (features.includes('photos') && review.dataset.hasPhoto !== 'true') return false;
        if (features.includes('videos') && review.dataset.hasVideo !== 'true') return false;
        if (features.includes('verified') && review.dataset.verified !== 'true') return false;
        if (features.includes('current-product') && review.dataset.productId !== currentProductId) return false;
        if (sort === 'only-pictures' && review.dataset.hasPhoto !== 'true') return false;
        return true;
      });

      const number = (review, key) => Number(review.dataset[key]) || 0;
      const date = (review) => Date.parse(review.dataset.date || '') || 0;
      filtered.sort((a, b) => {
        if (sort === 'oldest') return date(a) - date(b) || number(a, 'originalIndex') - number(b, 'originalIndex');
        if (sort === 'highest') return number(b, 'rating') - number(a, 'rating') || date(b) - date(a);
        if (sort === 'lowest') return number(a, 'rating') - number(b, 'rating') || date(b) - date(a);
        if (sort === 'helpful') return number(b, 'helpful') - number(a, 'helpful') || date(b) - date(a);
        if (sort === 'pictures-first') {
          return Number(b.dataset.hasPhoto === 'true') - Number(a.dataset.hasPhoto === 'true') || date(b) - date(a);
        }
        if (sort === 'videos-first') {
          return Number(b.dataset.hasVideo === 'true') - Number(a.dataset.hasVideo === 'true') || date(b) - date(a);
        }
        return date(b) - date(a) || number(a, 'originalIndex') - number(b, 'originalIndex');
      });
      return { filtered, activeFilterCount: ratings.length + features.length };
    }

    applyReviews(options = {}) {
      if (!this.reviewList) return;
      if (this.dataset.source === 'imported' && this.importState !== 'ready') {
        if (this.results) this.results.textContent = this.importState === 'error'
          ? 'Reviews could not be loaded. Please refresh the page to try again.' : 'Loading reviews…';
        if (this.emptyState) this.emptyState.hidden = true;
        if (this.noResults) this.noResults.hidden = true;
        if (this.pagination) this.pagination.hidden = true;
        if (this.loadMoreButton) this.loadMoreButton.hidden = true;
        return;
      }
      const { filtered, activeFilterCount } = this.getFilteredReviews();
      filtered.forEach((review) => this.reviewList.appendChild(review));
      this.reviews.filter((review) => !filtered.includes(review)).forEach((review) => {
        review.hidden = true;
      });

      let start = 0;
      let end = filtered.length;
      if (this.paginationStyle === 'numbered') {
        const totalPages = Math.max(1, Math.ceil(filtered.length / this.pageSize));
        this.currentPage = Math.min(this.currentPage, totalPages);
        start = (this.currentPage - 1) * this.pageSize;
        end = start + this.pageSize;
      } else if (this.paginationStyle !== 'all') {
        end = this.visibleCount;
      }

      filtered.forEach((review, index) => {
        review.hidden = index < start || index >= end;
      });

      if (this.results) {
        const term = filtered.length === 1 ? 'review' : 'reviews';
        this.results.textContent = `${filtered.length} ${term} found`;
      }
      if (this.filterCount) this.filterCount.textContent = activeFilterCount ? `(${activeFilterCount})` : '';
      if (this.clearFiltersButton) this.clearFiltersButton.hidden = activeFilterCount === 0;
      if (this.noResults) this.noResults.hidden = filtered.length !== 0 || this.reviews.length === 0;
      if (this.emptyState) this.emptyState.hidden = this.reviews.length !== 0;

      this.renderPagination(filtered.length);
      if (options.announce && this.results) this.results.focus?.({ preventScroll: true });
    }

    renderPagination(total) {
      if (this.pagination) {
        this.pagination.replaceChildren();
        this.pagination.hidden = this.paginationStyle !== 'numbered' || total <= this.pageSize;
        if (!this.pagination.hidden) {
          const pages = Math.ceil(total / this.pageSize);
          for (let page = 1; page <= pages; page += 1) {
            const button = document.createElement('button');
            button.type = 'button';
            button.dataset.reviewPage = String(page);
            button.textContent = String(page);
            button.setAttribute('aria-label', `Review page ${page}`);
            if (page === this.currentPage) button.setAttribute('aria-current', 'page');
            this.pagination.appendChild(button);
          }
        }
      }

      if (this.loadMoreButton) {
        const hasMore = this.visibleCount < total;
        this.loadMoreButton.hidden = ['numbered', 'all'].includes(this.paginationStyle) || !hasMore;
        this.loadMoreButton.textContent = this.paginationStyle === 'infinite' ? 'Load more reviews' : 'Load more reviews';
      }
    }

    revealMore(moveFocus) {
      const previouslyVisible = this.visibleCount;
      this.visibleCount += this.pageSize;
      this.applyReviews();
      if (!moveFocus) return;
      const visible = this.getFilteredReviews().filtered.filter((review) => !review.hidden);
      const firstNew = visible[previouslyVisible];
      if (firstNew) {
        firstNew.tabIndex = -1;
        firstNew.focus({ preventScroll: true });
        firstNew.scrollIntoView({ block: 'nearest' });
      }
    }

    setupInfiniteLoading() {
      if (this.paginationStyle !== 'infinite' || !('IntersectionObserver' in window)) return;
      const sentinel = this.querySelector('[data-review-load-sentinel]');
      if (!sentinel) return;
      this.infiniteObserver = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting) this.revealMore(false);
      }, { rootMargin: '240px 0px' });
      this.infiniteObserver.observe(sentinel);
    }

    openImage(trigger) {
      this.mediaTriggers = Array.from(this.querySelectorAll('[data-review-image]')).filter((button) => {
        return !button.closest('[data-review-item]')?.hidden;
      });
      this.activeMediaIndex = this.mediaTriggers.indexOf(trigger);
      this.lastMediaTrigger = trigger;
      this.renderImage();
      this.openLightbox();
    }

    renderImage() {
      const trigger = this.mediaTriggers?.[this.activeMediaIndex];
      if (!trigger || !this.lightboxContent) return;
      const image = document.createElement('img');
      image.src = trigger.dataset.fullSrc;
      image.alt = trigger.dataset.imageAlt || '';
      image.width = 1600;
      image.height = 1600;
      image.addEventListener('error', () => {
        const message = document.createElement('p');
        message.className = 'customer-reviews__media-error';
        message.setAttribute('role', 'alert');
        message.textContent = 'This review image could not be loaded.';
        this.lightboxContent.replaceChildren(message);
      }, { once: true });
      this.lightboxContent.replaceChildren(image);
      if (this.lightboxPosition) {
        this.lightboxPosition.textContent = `Image ${this.activeMediaIndex + 1} of ${this.mediaTriggers.length}`;
      }
      const multiple = this.mediaTriggers.length > 1;
      this.querySelector('[data-lightbox-previous]')?.toggleAttribute('hidden', !multiple);
      this.querySelector('[data-lightbox-next]')?.toggleAttribute('hidden', !multiple);
    }

    openVideo(trigger) {
      const template = this.querySelector(`#${CSS.escape(trigger.dataset.reviewVideo)}`);
      if (!template || !this.lightboxContent) return;
      this.lastMediaTrigger = trigger;
      this.activeMediaIndex = -1;
      this.lightboxContent.replaceChildren(template.content.cloneNode(true));
      if (this.lightboxPosition) this.lightboxPosition.textContent = 'Review video';
      this.querySelector('[data-lightbox-previous]')?.setAttribute('hidden', '');
      this.querySelector('[data-lightbox-next]')?.setAttribute('hidden', '');
      this.openLightbox();
    }

    openLightbox() {
      if (!this.lightbox) return;
      this.lightbox.showModal();
      document.body.classList.add('customer-reviews-lightbox-open');
      this.querySelector('[data-lightbox-close]')?.focus();
    }

    showAdjacentMedia(direction) {
      if (!this.mediaTriggers?.length || this.activeMediaIndex < 0) return;
      this.activeMediaIndex =
        (this.activeMediaIndex + direction + this.mediaTriggers.length) % this.mediaTriggers.length;
      this.renderImage();
    }

    onDialogClose() {
      document.body.classList.remove('customer-reviews-lightbox-open');
      this.lightboxContent?.replaceChildren();
      this.lastMediaTrigger?.focus({ preventScroll: true });
      this.lastMediaTrigger = null;
      this.activeMediaIndex = -1;
    }
  }

  customElements.define('customer-reviews', CustomerReviews);
}
